import React, {useState, useEffect, useCallback} from 'react'
import { connect, useDispatch } from 'react-redux';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { Link } from "react-router-dom";
import {
    startLoader,
    createMedicamentAction,
    importMedicamentFileUploadAction,
    updateMedicamentAction,
    getMedicamentsAction,
    deleteDataAction,
    downloadMedicamentTemplateAction
} from '../../../../store/actions/ProviderActions';
import { searchDrugsAction, searchDiseasesAction } from '../../../../store/actions/ConsultationActions';
import Select from 'react-select';
import swal from 'sweetalert'; 
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { TriStateCheckbox } from 'primereact/tristatecheckbox';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { currentUser } from "../../../../store/selectors/CurrentUserSelector";
import { useTranslation } from "react-i18next";
// import './Medicament.css';

const Medicament = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    // Initial states
    const initialFormState = {
        name: '',
        code: '',
        unit: '',
        presentation: '',
        forme: '',
        dosage: '',
        denomination: '',
        administration: '',
        refDosage: '',
        amount: 0,
        affections: [],
        isNeedAccord: false,
        isExclu: false,
        isActif: true
    };
    
    const initialErrorState = { name: '', code: '', unit: '', denomination: '' };

    // States
    const [formData, setFormData] = useState(initialFormState);
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState(initialErrorState);
    const [medicamentId, setMedicamentId] = useState('');
    const [addCard, setAddCard] = useState(false);
    const [updateMode, setUpdateMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null,
        filters: {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            unit: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            denomination: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },            
            amount: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            isExclu: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        }
    });

    // Unit types options
    const unitTypes = [
        {label: "Boîte", value: "Boîte"},
        {label: "Gellule", value: "Gellule"},
        {label: "Comprimé", value: "Comprimé"},
        {label: "Pilule", value: "Pilule"},
        {label: "Pastille", value: "Pastille"},
        {label: "Ovule", value: "Ovule"},
        {label: "Sachet", value: "Sachet"},
        {label: "Ampoule", value: "Ampoule"},
        {label: "Flacon", value: "Flacon"},
    ];

    // Helper to update form data
    const updateFormField = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Initialize filters
    const initFilters = useCallback(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            denomination: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }, 
            unit: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },            
            amount: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            isExclu: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    }, []);

    // Reset form
    const resetForm = useCallback(() => {
        setFormData(initialFormState);
        setFile(null);
        setErrors(initialErrorState);
        setMedicamentId('');
        setUpdateMode(false);
    }, [initialFormState, initialErrorState]);

    // Create mode
    useEffect(() => {
        resetForm();
        setAddCard(false);
    }, [props.successMessage]);

    // Download template handler
    const handleDownloadTemplate = useCallback(() => {
        dispatch(downloadMedicamentTemplateAction());
    }, [dispatch]);

    // Render header for data table
    const renderHeader = useCallback(() => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={onGlobalFilterChange} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    }, [globalFilterValue, t]);

    // Clear filter
    const clearFilter = useCallback(() => {
        initFilters();
    }, [initFilters]);

    // Define columns
    const setColumns = useCallback(() => {
        return [
            {name: 'code', headerName: "Code", filterPlaceholder: t("provider.code-filter"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'name', headerName: t('common.name'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'denomination', headerName: t("common.denomination"), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'unit', headerName: t('common.unit'), filterPlaceholder: t("common.filter-unity"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'amount', dataType: 'numeric', headerName: t('common.amount'), filterPlaceholder: t('facturation.filter-by-amount'), style: { minWidth: '6rem', fontSize: '14px' }, filter: true, bodyTemplate: null},
            {name: 'isExclu', headerName: t('special-acts.cover'), filterPlaceholder: t("common.filter-coverage"), style: { minWidth: '12rem', fontSize: '14px' }, filter: true, bodyTemplate: statusBordBodyTemplate, filterElement: statusBordFilterTemplate},
            {name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: t("patient.created-on"), filterPlaceholder: t('facturation.create-date-filter'), style: { minWidth: '6rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '6rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    }, [t]);

    // Global filter change handler
    const onGlobalFilterChange = useCallback((e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);

        if(value.trim().length > 0) {
            dispatch(searchDrugsAction(value)); 
        } else {
            dispatch(searchDrugsAction("@@@"));
        }
    }, [filters, dispatch]);

    // Actions template
    const getActionsTemplate = useCallback((rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'UPDATEMEDICAMENT') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => updateAct(e, rowData)}
                        title={t("common.edit")}
                        data-testid={'update-button-'+rowData.id}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'DELETEMEDICAMENT') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t("common.delete")}
                        data-testid={'delete-button-'+rowData.id}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    }, [t]);

    // Delete handler
    const handleDelete = useCallback((rowData) => {
        swal({
            title: t("patient.delete-drug") + rowData.name + " ?",
            text: t("patient.delete-message-drug"),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                const data = { type: "MEDICAMENT", id: rowData.id };
                dispatch(startLoader());
                dispatch(deleteDataAction(data));
            }
        });
    }, [t, dispatch]);

    // Update data handler
    const updateAct = useCallback((e, drug) => {
        e.preventDefault();
        setFormData({
            name: drug.name || '',
            code: drug.code || '',
            unit: drug.unit || '',
            presentation: drug.presentation || '',
            forme: drug.forme || '',
            dosage: drug.dosage || '',
            denomination: drug.denomination || '',
            administration: drug.administration || '',
            refDosage: drug.refDosage || '',
            amount: drug.amount || 0,
            affections: drug.affections || [],
            isNeedAccord: drug.isNeedAccord || false,
            isExclu: drug.isExclu || false,
            isActif: drug.isActif || false
        });
        setMedicamentId(drug.id);
        setUpdateMode(true);
        setAddCard(true);
    }, []);
    // Create mode
    const createMode = useCallback(() => {
        resetForm();
        setAddCard(true);
    }, [resetForm]);

    // Status templates
    const statusBordFilterTemplate = useCallback((options) => {
        return (
            <div className="flex align-items-center gap-2">
                <label htmlFor="isClose-filter" className="font-bold">
                    Couvert
                </label>
                <TriStateCheckbox inputId="isClose-filter" value={options.value} onChange={(e) => options.filterCallback(e.value)} />
            </div>
        );
    }, []);

    const statusBordBodyTemplate = useCallback((rowData) => {
        return <Tag value={rowData.isExclu ? 'Non Couvert' : "Couvert"} severity={getSeverity(rowData.isExclu)} />;
    }, []);

    const getSeverity = useCallback((status) => {
        return status ? 'danger' : 'success';
    }, []);

    // Date templates
    const dateFilterTemplate = useCallback((options) => {
        return (
            <Calendar 
                value={options.value} 
                onChange={(e) => options.filterCallback(e.value, options.index)} 
                dateFormat="dd/mm/yy" 
                placeholder="dd/mm/yyyy" 
                mask="99/99/9999" 
            />
        );
    }, []);

    const datePaiementBodyTemplate = useCallback((rowData) => {
        return formatDate(rowData.createdAt);
    }, []);

    const formatDate = useCallback((value) => {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, []);

    // Permission checks
    const canDo = useCallback((menuCode, actionCode) => {
        const theMenu = props.currentUser.menus?.find(menu => menu.code === menuCode);
        return theMenu ? theMenu.actionMenus?.some(action => action.code === actionCode) : false;
    }, [props.currentUser.menus]);
    
    const hasMenu = useCallback((menuCode) => {
        return props.currentUser.menus?.some(menu => menu.code === menuCode) || false;
    }, [props.currentUser.menus]);

    // File selection handler
    const selectFile = useCallback((event) => {
        setFile(event.target.files[0]);
    }, []);

    // Save handler
    const onMedicamentSave = useCallback((e) => {
        e.preventDefault();
        
        // Handle file upload
        if(file) {
            dispatch(startLoader());
            dispatch(importMedicamentFileUploadAction(file));
            return;
        }
        
        // Validate form
        let error = false;
        const errorObj = { ...initialErrorState };
        
        if (!formData.code.trim()) {
            errorObj.code = t("common.code-required");
            error = true;
        }
        
        if (!formData.name.trim()) {
            errorObj.name = t("common.name-required");
            error = true;
        }
        
        if (!formData.unit.trim()) {
            errorObj.unit = t("common.unit-required");
            error = true;
        }
        
        if (!formData.denomination.trim()) {
            errorObj.denomination = t("common.denomination-required");
            error = true;
        }
        
        setErrors(errorObj);
        
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        dispatch(startLoader());
        
        const obj = {
            name: formData.name,
            code: formData.code,
            unit: formData.unit,
            presentation: formData.presentation,
            forme: formData.forme,
            dosage: formData.dosage,
            denomination: formData.denomination,
            administration: formData.administration,
            refDosage: formData.refDosage,
            affections: formData.affections,
            amount: formData.amount,
            isNeedAccord: formData.isNeedAccord,
            isExclu: formData.isExclu,
            isActif: formData.isActif
        };
        
        if (updateMode) {
            dispatch(updateMedicamentAction(medicamentId, obj));
        } else {
            dispatch(createMedicamentAction(obj));
        }
        
        setAddCard(false);
    }, [dispatch, file, formData, initialErrorState, t, medicamentId, updateMode]);

    // Pagination handlers
    const onGoToPage = useCallback((event) => {
        setlazyState(event);
    }, []);

    const onFilterData = useCallback((rowData) => {
        // Filter logic if needed
    }, []);

    const onSortData = useCallback((rowData) => {
        // Sort logic if needed
    }, []);

    // Load data on component mount
    useEffect(() => {
        initFilters();
        dispatch(getMedicamentsAction(currentPage, 10));
    }, [dispatch, initFilters, currentPage]);

    // Handle lazy loading
    useEffect(() => {
        if(lazyState.page !== props.medicamentsPages?.pageNumber) {
            dispatch(startLoader());
            dispatch(getMedicamentsAction(lazyState.page, lazyState.rows)); 
        }
    }, [lazyState, dispatch, props.medicamentsPages?.pageNumber]);

    // Reset form when data changes
    useEffect(() => {
        resetForm();
        setAddCard(false);
    }, [props.successMessage]);

    return (
        <>
            {/* Header modernisé */}
            <div className="provider-type-header mb-0 form-head bg-white p-3 bt-5 border-top-solid">
                <div>
                    <h4 className="mb-1">{t("common.medications")}</h4>
                    <p className="text-muted mb-0 small">{t("common.manage-medications")}</p>
                </div>
                <div className="d-flex gap-2">
                    {/* Download Template Button */}
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleDownloadTemplate}
                    >
                        <i className="fa fa-download me-1"></i> {t("common.download-template")}
                    </button>
                    
                    {/* Create Button */}
                    {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'CREATEMEDICAMENT') && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={createMode}
                        >
                            <i className="fa fa-plus-circle me-1"></i> {t("common.new-drug")}
                        </button>
                    )}
                </div>
            </div>
            
            {/* Modal modernisée */}
            <Modal 
                show={addCard} 
                onHide={() => setAddCard(false)}
                size="xl"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("common.modify-drug") + " " + formData.name : t("common.create-new-drug")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form className="row">
                        {/* Section 1: Informations de base */}
                        <div className="col-12 mb-4">
                            <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.basic-info")}</h6>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t('common.code')} *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                placeholder="MED001"
                                value={formData.code}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('code', e.target.value)}
                            />
                            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t('common.label')} *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder={t("common.drug")}
                                value={formData.name}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('name', e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t("common.denomination")} *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.denomination ? 'is-invalid' : ''}`}
                                placeholder={t("common.denomination")}
                                value={formData.denomination}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('denomination', e.target.value)}
                            />
                            {errors.denomination && <div className="invalid-feedback">{errors.denomination}</div>}
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t("common.Unit")} *</label>
                            <Select
                                className={`select-search ${errors.unit ? 'is-invalid' : ''}`}
                                value={unitTypes.filter(elt => elt.value === formData.unit)}
                                placeholder="Choisir une unité"
                                onChange={(val) => updateFormField('unit', val?.value || '')}
                                isDisabled={props.showLoading || file}
                                options={unitTypes}
                                isClearable
                            />
                            {errors.unit && <div className="invalid-feedback d-block">{errors.unit}</div>}
                        </div>

                        {/* Section 2: Détails du médicament */}
                        <div className="col-12 mb-3 mt-4">
                            <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.medication-details")}</h6>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t("common.presentation")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("common.presentation")}
                                value={formData.presentation}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('presentation', e.target.value)}
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t("common.form")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("common.form")}
                                value={formData.forme}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('forme', e.target.value)}
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Dosage</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Dosage"
                                value={formData.dosage}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('dosage', e.target.value)}
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Administration</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Mode d'administration"
                                value={formData.administration}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('administration', e.target.value)}
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Référence de dosage</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Référence de dosage"
                                value={formData.refDosage}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('refDosage', e.target.value)}
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label className="form-label">{t('common.amount')}</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                placeholder="0"
                                value={formData.amount}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('amount', parseFloat(e.target.value) || 0)}
                            />
                        </div>

                        {/* Section 3: Affections */}
                        <div className="col-12 mb-3 mt-4">
                            <h6 className="text-primary border-bottom pb-2 mb-3">Affections</h6>
                        </div>
                        
                        <div className="col-12 mb-3">
                            <label className="form-label">Affections traitées</label>
                            <Select
                                className="select-search"
                                options={props.diseases.map(elt => ({label: elt.name, value: elt.code}))}
                                value={formData.affections.map(elt => ({label: elt.affectionName, value: elt.affectionCode}))}
                                placeholder={t('consultation.enter-keyword-affliction')}
                                isMulti
                                onInputChange={(val) => {
                                    if(val.length >= 1){
                                        dispatch(searchDiseasesAction(val));
                                    }
                                }}
                                onChange={(vals) => {
                                    updateFormField('affections', vals ? vals.map(val => ({
                                        affectionCode: val.value, 
                                        affectionName: val.label
                                    })) : []);
                                }}
                                isDisabled={props.showLoading || file}
                            />
                        </div>

                        {/* Section 4: Options */}
                        <div className="col-12 mb-3 mt-4">
                            <h6 className="text-primary border-bottom pb-2 mb-3">Options</h6>
                        </div>
                        
                        <div className="col-md-4 mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="isExclu"
                                    checked={formData.isExclu}
                                    disabled={props.showLoading || file}
                                    onChange={(e) => updateFormField('isExclu', e.target.checked)}
                                />
                                <label className="form-check-label text-danger fw-bold" htmlFor="isExclu">
                                    {t("common.rejecte")}
                                </label>
                            </div>
                        </div>
                        
                        <div className="col-md-4 mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="isNeedAccord"
                                    checked={formData.isNeedAccord}
                                    disabled={props.showLoading || file}
                                    onChange={(e) => updateFormField('isNeedAccord', e.target.checked)}
                                />
                                <label className="form-check-label text-warning fw-bold" htmlFor="isNeedAccord">
                                    {t("common.agreement")}
                                </label>
                            </div>
                        </div>
                        
                        <div className="col-md-4 mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="isActif"
                                    checked={formData.isActif}
                                    disabled={props.showLoading || file}
                                    onChange={(e) => updateFormField('isActif', e.target.checked)}
                                />
                                <label className="form-check-label text-success fw-bold" htmlFor="isActif">
                                    {t("common.asset")}
                                </label>
                            </div>
                        </div>

                        {/* Section 5: Import CSV */}
                        <div className="col-12 mt-4">
                            <hr className="my-4" />
                            <h6 className="text-primary mb-3">{t("common.import-csv-act")}</h6>
                            <div className="input-group">
                                <span className="input-group-text">CSV</span>
                                <input 
                                    type="file" 
                                    className="form-control" 
                                    onChange={selectFile}
                                    accept=".csv"
                                />
                            </div>
                            {file && (
                                <div className="alert alert-info mt-2 py-2">
                                    <small>{t("common.selected-file")}: <strong>{file.name}</strong></small>
                                </div>
                            )}
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddCard(false)}
                        disabled={props.showLoading}
                    >
                        {t("common.cancel")}
                    </button>
                    
                    {props.showLoading ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t("common.saving")}</span>
                        </Button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={onMedicamentSave}
                        >
                            {updateMode ? t("common.update") : t("common.save")}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
            
            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={ 
                            globalFilterValue.length > 0 && props.drugs.length > 0 ? 
                            props.drugs.map(acte => {
                                acte.createdAt = new Date(acte.createdAt);
                                return acte;
                            }) :
                            (props.medicamentsPages.data && props.medicamentsPages?.data.length > 0 ? props.medicamentsPages.data.map(acte => {
                                acte.createdAt = new Date(acte.createdAt);
                                return acte;
                            }) : [])
                        }
                        emptyMessage={t('common.no-drug')} 
                        filters={filters}
                        globalFiltersFields={['code', 'name', 'unit', 'denomination', 'amount', 'isExclu', 'createdAt']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: 'Code', dataKey: 'code' },
                            { title: t('common.name'), dataKey: 'name' },
                            { title: t("common.Unit"), dataKey: 'unit' },
                            { title: t("common.denomination"), dataKey: 'denomination' },
                            { title: t('facturation.amount'), dataKey: 'amount' },
                            { title: t("common.rejecte"), dataKey: 'isExclu' },
                            { title: t('patient.created-on'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename={"médicaments"}
                        loading={props.showLoading}
                        lazy={globalFilterValue.length === 0}
                        dataSet={lazyState}
                        totalRecords={props.medicamentsPages?.totalPages}
                        onGoToPage={onGoToPage}
                        onSortData={onSortData}
                        onFilterData={onFilterData}
                    />
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        medicamentsPages: state.provider.medicamentsPages,
        drugs: state.consultation.drugs,
        diseases: state.consultation.diseases,
        errorMessage: state.clientAuth.errorMessage,
        successMessage: state.clientAuth.successMessage,
        showLoading: state.provider.showLoading,
        currentUser: currentUser(state)
    };
};

export default connect(mapStateToProps)(Medicament);